import { Dropdown, Icon } from '@ps/isp-web-component-library';
import { ChevronMediumDown } from '@ps/isp-web-component-library/icons/ChevronMediumDown';
import type { CompetitionsQuickSwitcherData } from '@ps/isp-web-services-types';
import type { IspSportsAlias } from '@ps/web-sports-config';
import classNames from 'clsx';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { CardTitle, DefaultLink } from '@swf/sports/components';
import { DROPDOWN_HEIGHT_ADJUST } from '@swf/sports/utils/constants';

import styles from './Competitions.scss';

interface CompetitionsProps {
  title: React.ReactNode;
  quickSwitcherData: CompetitionsQuickSwitcherData;
  isNavigating?: boolean;
}

const getDefaultLinkAlias = (alias?: IspSportsAlias): IspSportsAlias => {
  const isOutrightsRoute = Boolean(alias === 'sportsCompetitionOutrights');
  const isGolfMatchesRoute = Boolean(alias === 'sportsGolfMatches');

  if (isGolfMatchesRoute) return 'sportsGolfMatches';
  if (isOutrightsRoute) return 'sportsCompetitionOutrights';
  return 'sportsCompetition';
};

const Competitions: React.FC<CompetitionsProps> = ({ title, quickSwitcherData }) => {
  const { quickSwitcher } = useDispatch<Dispatch>();

  const { eventId } = useSelector(select.sports.getUrlParams);
  const alias = useSelector(select.cms.getPage)?.alias as IspSportsAlias;

  const isLoading = useSelector(select.quickSwitcher.isLoading);
  const isNavigating = useSelector(select.quickSwitcher.isNavigating);
  const shouldShowQuickSwitcher = quickSwitcherData.items.length > 1;

  const handleDropdownClose = () => {
    if (!eventId && !isNavigating) return;

    quickSwitcher.quickSwitcherRequested({ hierarchyLevel: 'event', facetId: eventId });
    quickSwitcher.navigating(false);
  };

  if (!shouldShowQuickSwitcher) return <CardTitle>{title}</CardTitle>;

  return (
    <Dropdown
      isOpenCallback={handleDropdownClose}
      isDefaultOpen={isNavigating}
      title={quickSwitcherData.title}
      verticalOffset="XXS"
      triggerEl={({ arrowRef, ...props }) => (
        <CardTitle {...props} className={classNames({ [styles.loading]: isLoading })} isDropdown>
          {title}
          <Icon ref={arrowRef} icon={ChevronMediumDown} size="sm" />
        </CardTitle>
      )}
      overlay
      overrideHeightAdjust={DROPDOWN_HEIGHT_ADJUST}
    >
      {quickSwitcherData.items.map((competitionId) => {
        const competitionAttachment = quickSwitcherData.competitions[competitionId];
        const { eventTypeSlug, eventTypeId } =
          quickSwitcherData.eventTypes[competitionAttachment.eventTypeId] || {};

        return (
          <DefaultLink
            key={competitionId}
            alias={getDefaultLinkAlias(alias)}
            values={{
              eventTypeSlug,
              eventTypeId,
              competitionSlug: competitionAttachment.competitionSlug,
              competitionId
            }}
          >
            {competitionAttachment.competitionName}
          </DefaultLink>
        );
      })}
    </Dropdown>
  );
};

export default Competitions;
